import React, { useState } from 'react';
import { 
  Grid, TextField, MenuItem, Button, Box, Typography, 
  InputAdornment, Chip, Card, CardContent, Collapse, IconButton,
  Stack, Divider
} from '@mui/material';
import TooltipWrapper from './TooltipWrapper';
import { dashboardTooltips } from '../../utils/tooltips';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Category as CategoryIcon,
  Psychology as StrategyIcon,
  ShowChart as SymbolIcon,
  DateRange as DateIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterIcon,
  LocalOffer as TagIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';

const TradeFilters = ({
  selectedCategory = '',
  selectedSymbol = '',
  selectedStrategy = '',
  selectedStatus = '',
  selectedTags = [],
  startDate = null,
  endDate = null,
  groupBy = 'symbol',
  onCategoryChange,
  onSymbolChange,
  onStrategyChange,
  onStatusChange,
  onTagsChange,
  onStartDateChange,
  onEndDateChange,
  onTimeFilter,
  onGroupByChange,
  symbols = [],
  strategies = [],
  statuses = [],
  allTags = [],
  strategyNamesMap = {}
}) => {
  const { settings } = useSettings();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const timeFilterButtons = [
    { label: 'Today', value: 'today', color: 'primary' },
    { label: 'Yesterday', value: 'yesterday', color: 'secondary' },
    { label: 'This Week', value: 'thisWeek', color: 'info' },
    { label: 'Last Week', value: 'lastWeek', color: 'info' },
    { label: 'This Month', value: 'thisMonth', color: 'success' },
    { label: 'Last Month', value: 'lastMonth', color: 'success' },
    { label: 'Last 3 Months', value: 'last3Months', color: 'warning' },
    { label: 'This Year', value: 'thisYear', color: 'error' },
    { label: 'Last Year', value: 'lastYear', color: 'error' },
    { label: 'Reset', value: 'reset', color: 'default' }
  ];

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent sx={{ pb: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Trade Filters
          </Typography>
          <IconButton 
            onClick={() => setShowAdvanced(!showAdvanced)}
            size="small"
            sx={{ 
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' }
            }}
          >
            {showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Quick Time Filters */}
        {onTimeFilter && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <TooltipWrapper title={dashboardTooltips.quickTimeFilters} showIcon={true}>
                <Typography variant="subtitle2" color="text.secondary">
                  Quick Time Filters
                </Typography>
              </TooltipWrapper>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {timeFilterButtons.map((btn) => (
                <Chip
                  key={btn.value}
                  label={btn.label}
                  onClick={() => onTimeFilter(btn.value)}
                  variant={btn.value === 'reset' ? 'outlined' : 'filled'}
                  color={btn.color}
                  size="small"
                  clickable
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: 1
                    },
                    transition: 'all 0.2s ease'
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Tag Filters */}
        {(allTags.length > 0 || settings?.defaultTags?.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TagIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <TooltipWrapper title={dashboardTooltips.filterByTags} showIcon={true}>
                <Typography variant="subtitle2" color="text.secondary">
                  Filter by Tags
                </Typography>
              </TooltipWrapper>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {[...new Set([...(settings?.defaultTags || []), ...allTags])].map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onClick={() => {
                    const newTags = selectedTags.includes(tag)
                      ? selectedTags.filter(t => t !== tag)
                      : [...selectedTags, tag];
                    onTagsChange(newTags);
                  }}
                  variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
                  color={selectedTags.includes(tag) ? 'primary' : 'default'}
                  size="small"
                  clickable
                  sx={{
                    borderRadius: 2,
                    fontWeight: selectedTags.includes(tag) ? 600 : 400,
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: 1
                    },
                    transition: 'all 0.2s ease'
                  }}
                />
              ))}
            </Stack>
            <Divider sx={{ mt: 3, mb: 3 }} />
          </Box>
        )}

        {/* Main Filters */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField 
              select 
              label="Category" 
              value={selectedCategory} 
              onChange={(e) => onCategoryChange(e.target.value)} 
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="stock">📈 Stock</MenuItem>
              <MenuItem value="crypto">₿ Crypto</MenuItem>
              <MenuItem value="forex">💱 Forex</MenuItem>
              <MenuItem value="derivative">📊 Derivative</MenuItem>
              <MenuItem value="commodity">🥇 Commodity</MenuItem>
              <MenuItem value="index">📉 Index</MenuItem>
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField 
              select 
              label="Status" 
              value={selectedStatus} 
              onChange={(e) => onStatusChange ? onStatusChange(e.target.value) : null} 
              fullWidth
              variant="outlined"
              disabled={!onStatusChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {statuses.map((status, i) => (
                <MenuItem key={i} value={status}>
                  {status === 'WIN' ? '✅ WIN' : 
                   status === 'LOSS' ? '❌ LOSS' : 
                   status === 'OPEN' ? '⏳ OPEN' : 
                   status === 'ANALYSIS' ? '🔍 ANALYSIS' : status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField 
              select 
              label="Strategy" 
              value={selectedStrategy} 
              onChange={(e) => onStrategyChange(e.target.value)} 
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StrategyIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            >
              <MenuItem value="">All Strategies</MenuItem>
              {strategies.map((s, i) => (
                <MenuItem key={i} value={s}>{strategyNamesMap[s] || s}</MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField 
              select 
              label="Symbol" 
              value={selectedSymbol} 
              onChange={(e) => onSymbolChange(e.target.value)} 
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SymbolIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            >
              <MenuItem value="">All Symbols</MenuItem>
              {symbols.map((s, i) => (
                <MenuItem key={i} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker 
                label="Start Date" 
                value={startDate} 
                onChange={onStartDateChange}
                sx={{ 
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }} 
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker 
                label="End Date" 
                value={endDate} 
                onChange={onEndDateChange}
                sx={{ 
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }} 
              />
            </LocalizationProvider>
          </Grid>
          
          {onGroupByChange && (
            <Grid item xs={12} sm={6} md={2}>
              <TextField 
                select 
                label="Group By" 
                value={groupBy} 
                onChange={(e) => onGroupByChange(e.target.value)} 
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }
                }}
              >
                <MenuItem value="symbol">📊 By Symbol</MenuItem>
                <MenuItem value="strategy">🎯 By Strategy</MenuItem>
                <MenuItem value="date">📅 By Date</MenuItem>
              </TextField>
            </Grid>
          )}
        </Grid>


      </CardContent>
    </Card>
  );
};

export default TradeFilters;